import {
  Box,
  Button,
  Grid,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ErrorMessage, Form, Formik, FormikProps } from "formik";
import { useRef, useState, useEffect } from "react";
import * as Yup from "yup";
import {
  addStoreSettings,
  addSiteCredentials,
  useValidateBCCredentialsMutation,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
interface BigCommerceSettingProps {
  handleNext: any;
  handleBack: any;
}
interface BigCommerceFormValues {
  storeUrl: string;
  accessToken: string;
}
export const BigCommerceValidationSchema = Yup.object().shape({
  storeUrl: Yup.string()
    .required("Store Url is required")
    .url("Please enter a valid Store Url")
    .matches(
      /^https:\/\/api\.bigcommerce\.com\/stores\/[a-zA-Z0-9]+$/,
      "Store Url must be in format: https://api.bigcommerce.com/stores/{store_hash}"
    ),
  accessToken: Yup.string()
    .required("Access Token is required")
    .min(10, "Access Token must be at least 10 characters"),
});

export const BigCommerceSetting: React.FunctionComponent<
  BigCommerceSettingProps
> = ({ handleNext, handleBack }) => {
  const formRef = useRef<FormikProps<any>>(null);
  const errorMessage =
    "Failed to connect. Please check your credentials and try again.";
  const dmsDealerId = useSelector(
    (state: AppState) => state.authReducer.dmsDealerId
  );

  const [connectionStatus, setConnectionStatus] = useState({
    isProcessed: false,
    isValid: false,
    errorMessage: "",
  });
  const dispatch = useDispatch();
  const [validateCredentials, validateCredentialsResult] =
    useValidateBCCredentialsMutation();
  const handleConnection = async (values: BigCommerceFormValues) => {
    validateCredentials({
      StoreUrl: values.storeUrl,
      AccessToken: values.accessToken,
      dmsDealerId: dmsDealerId || "",
    }).then((result: any) => {
      if (result.data && result.data.status === 200) {
        setConnectionStatus({
          isProcessed: true,
          isValid: true,
          errorMessage: "",
        });
        dispatch(
          addStoreSettings({
            token: values.accessToken,
            url: values.storeUrl,
            siteName: result.data.entity?.name,
          })
        );
        let siteCredentials = {
          storeUrl: values.storeUrl,
          storeAccessToken: values.accessToken,
          siteDomain: result.data.entity?.secureUrl,
        };
        dispatch(
          addSiteCredentials({ credentials: JSON.stringify(siteCredentials) })
        );
      } else {
        setConnectionStatus({
          isProcessed: true,
          isValid: false,
          errorMessage: result.data.message || errorMessage,
        });
      }
    });
  };

  return (
    <Box minHeight={"80vh"}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          p: 0.5,
          mt: 2,
        }}
      >
        <Button variant="outlined" size="small" onClick={handleBack}>
          Back
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleNext}
          disabled={!(connectionStatus.isProcessed && connectionStatus.isValid)}
        >
          Next
        </Button>
      </Box>

      <Grid container style={{ justifyContent: "center" }}>
        <Grid item xs={12} sm={6}>
          <Formik
            initialValues={{
              storeUrl: "",
              accessToken: "",
            }}
            enableReinitialize
            validationSchema={BigCommerceValidationSchema}
            onSubmit={(values) => {
              handleConnection(values);
            }}
            innerRef={formRef}
          >
            {({ values, handleChange, handleBlur }) => (
              <Form>
                <FormControl
                  sx={{ m: 3 }}
                  component="fieldset"
                  variant="standard"
                  style={{ alignItems: "center" }}
                >
                  <FormLabel component="h2" sx={{ fontSize: "1.5rem", mb: 3 }}>
                    BigCommerce Authentication Setting
                  </FormLabel>

                  <Grid container spacing={3} p={2}>
                    {/* Status Alert */}
                    <Grid item sm={12}>
                      {connectionStatus.isProcessed && (
                        <Alert
                          severity={
                            connectionStatus.isValid ? "success" : "error"
                          }
                        >
                          {connectionStatus.isValid
                            ? "Successfully connected to BigCommerce!"
                            : connectionStatus.errorMessage}
                        </Alert>
                      )}
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="storeUrl"
                        name="storeUrl"
                        label="Store Url"
                        fullWidth
                        variant="standard"
                        placeholder="https://api.bigcommerce.com/stores/store_hash"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.storeUrl}
                        disabled={connectionStatus.isValid}
                        helperText={<ErrorMessage name="storeUrl" />}
                        error={
                          !!formRef.current?.errors?.storeUrl &&
                          !!formRef.current?.touched?.storeUrl
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="accessToken"
                        name="accessToken"
                        label="Access Token"
                        type="password"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.accessToken}
                        disabled={connectionStatus.isValid}
                        helperText={<ErrorMessage name="accessToken" />}
                        error={
                          !!formRef.current?.errors?.accessToken &&
                          !!formRef.current?.touched?.accessToken
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <Button
                        variant="contained"
                        type="submit"
                        size="small"
                        disabled={
                          validateCredentialsResult.isLoading ||
                          connectionStatus.isValid
                        }
                        startIcon={
                          validateCredentialsResult.isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                      >
                        {validateCredentialsResult.isLoading
                          ? "Connecting..."
                          : "Connect to BigCommerce"}
                      </Button>
                    </Grid>
                  </Grid>
                </FormControl>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BigCommerceSetting;
